// src/components/ContactUpload.jsx
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axiosInstance from "../utils/axiosInstance";
import { ArrowLeft, Download, UploadCloud, FileSpreadsheet, AlertTriangle } from "lucide-react";

export default function ContactUpload() {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dropzoneKey, setDropzoneKey] = useState(0);

    const handleDrop = (accepted) => {
        const valid = accepted.filter((f) =>
            ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(f.type)
        );
        if (valid.length !== accepted.length) toast.error("Only CSV or Excel files are allowed.");
        if (valid.length !== 1) return toast.error("Upload exactly one file.");

        const file = valid[0];
        setUploadedFile(file);

        const reader = new FileReader();
        reader.onload = () => {
            if (file.type === "text/csv") {
                Papa.parse(reader.result, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (res) => setParsedData(res.data),
                    error: () => toast.error("Error parsing CSV."),
                });
            } else {
                try {
                    const wb = XLSX.read(reader.result, { type: "binary" });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    setParsedData(XLSX.utils.sheet_to_json(sheet));
                } catch {
                    toast.error("Error parsing Excel.");
                }
            }
        };
        file.type === "text/csv" ? reader.readAsText(file) : reader.readAsBinaryString(file);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "text/csv": [".csv"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
        maxFiles: 1,
    });

    const handleDownloadTemplate = () => {
        const cols = [
            "Contact Name",
            "Profile URL",
            "Company Name",
            "Company LinkedIn",
            "Position",
            "AI Position",
            "Company Website",
            "Email",
            "Business Email",
            "Phone",
            "Follower Count",
            "Sales Nav Link",
            "Status",
            "Dynamics Entity",
            "Dynamics Owner",
        ];

        const sample = [
            "Jane Doe",
            "https://www.linkedin.com/in/janedoe",
            "CiiLOCK",
            "https://www.linkedin.com/company/ciilock/",
            "Engineer",
            "Engineer (AI)",
            "https://www.ciilock.com",
            "jane@ciilock.com",
            "jane.business@ciilock.com",
            "+61 3 9000 0000",
            "1250",
            "https://www.linkedin.com/sales/people/XXXX",
            "unconnected",
            "Lead",
            "Tom",
        ];

        const ws = XLSX.utils.aoa_to_sheet([cols, sample]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Contacts");
        XLSX.writeFile(wb, "contact_upload_template.xlsx");
    };

    const handleSubmit = async () => {
        if (!uploadedFile) return toast.error("Upload a file first.");
        if (parsedData.length > 5000) return toast.warning("Max 5000 rows.");

        const fd = new FormData();
        fd.append("file", uploadedFile);

        setIsSubmitting(true);
        try {
            const res = await axiosInstance.post("/contact/file/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Created ${res.data.created}, updated ${res.data.updated}, skipped ${res.data.skipped}.`);
            setUploadedFile(null);
            setParsedData([]);
            setDropzoneKey((k) => k + 1);
        } catch (e) {
            const msg = e.response?.data?.message || e.message;
            toast.error("Upload failed: " + msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const previewCols = useMemo(() => {
        if (!parsedData?.length) return [];
        return Object.keys(parsedData[0] || {});
    }, [parsedData]);

    const previewRows = useMemo(() => parsedData.slice(0, 10), [parsedData]);

    // UI helpers (consistent, 44px controls, rounded-2xl cards)
    const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const btn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:opacity-60";
    const btnPrimary = "bg-slate-900 text-white hover:bg-slate-800";
    const btnGhost = "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50";
    const btnSlate = "bg-slate-900 text-white hover:bg-slate-800";
    const note =
        "rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900";
    const kbd =
        "rounded-lg border border-slate-200 bg-white px-2 font-mono text-sm text-slate-700";

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="mx-auto w-full max-w-7xl space-y-6">
                {/* Header */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Contact Upload</h1>
                        <p className="mt-1 text-sm text-slate-600">
                            Upload a CSV or XLSX to create/update contacts (max 5,000 rows).
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button type="button" onClick={() => window.history.back()} className={`${btn} ${btnGhost}`}>
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </button>

                        <button type="button" onClick={handleDownloadTemplate} className={`${btn} ${btnSlate}`}>
                            <Download className="h-4 w-4" />
                            Download Example
                        </button>
                    </div>
                </div>

                {/* Note */}
                <div className={note}>
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                            <AlertTriangle className="h-4 w-4 text-amber-700" />
                        </div>
                        <div className="space-y-1">
                            <div className="font-semibold">Column requirements</div>
                            <div className="text-amber-900/90">
                                Required: <span className={kbd}>Contact Name</span>. Optional:{" "}
                                <span className={kbd}>Profile URL</span>, <span className={kbd}>Company Name</span>,{" "}
                                <span className={kbd}>Company LinkedIn</span>, <span className={kbd}>Position</span>,{" "}
                                <span className={kbd}>AI Position</span>, <span className={kbd}>Company Website</span>,{" "}
                                <span className={kbd}>Email</span>, <span className={kbd}>Business Email</span>,{" "}
                                <span className={kbd}>Phone</span>, <span className={kbd}>Follower Count</span>,{" "}
                                <span className={kbd}>Sales Nav Link</span>, <span className={kbd}>Status</span>,{" "}
                                <span className={kbd}>Dynamics Entity</span>, <span className={kbd}>Dynamics Owner</span>.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Upload card */}
                <div className={`${card} p-4`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="space-y-1">
                            <div className="text-sm font-semibold text-slate-800">Upload file</div>
                            <div className="text-sm text-slate-600">
                                Drag & drop a <span className="font-semibold">CSV</span> or <span className="font-semibold">XLSX</span>, or click to browse.
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className={`${btn} ${btnPrimary}`}
                        >
                            <UploadCloud className={`h-4 w-4 ${isSubmitting ? "animate-pulse" : ""}`} />
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>

                    <div className="mt-4">
                        <div
                            key={dropzoneKey}
                            {...getRootProps()}
                            className={[
                                "group relative rounded-2xl border-2 border-dashed p-10 text-center transition",
                                "cursor-pointer",
                                uploadedFile
                                    ? "border-slate-400 bg-slate-100/60"
                                    : "border-slate-200 bg-white hover:bg-slate-50",
                            ].join(" ")}
                        >
                            <input {...getInputProps()} />

                            <div className="mx-auto flex max-w-xl flex-col items-center gap-3">
                                <div
                                    className={[
                                        "flex h-12 w-12 items-center justify-center rounded-2xl border shadow-sm",
                                        uploadedFile ? "border-slate-300 bg-white" : "border-slate-200 bg-white",
                                    ].join(" ")}
                                >
                                    <FileSpreadsheet className="h-5 w-5 text-slate-600" />
                                </div>

                                <div className="text-sm font-semibold text-slate-800">
                                    Drag & drop a file here, or click to select
                                </div>
                                <div className="text-xs text-slate-500">
                                    Accepted: <span className="font-semibold">.csv</span>, <span className="font-semibold">.xlsx</span> • Max 1 file
                                </div>

                                {uploadedFile && (
                                    <div className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold text-slate-900">
                                                    {uploadedFile.name}
                                                </div>
                                                <div className="mt-0.5 text-xs text-slate-500">
                                                    Parsed rows: <span className="font-semibold text-slate-800">{parsedData.length}</span>
                                                    {parsedData.length > 5000 ? (
                                                        <span className="ml-2 font-semibold text-rose-600">Over limit</span>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {uploadedFile.type === "text/csv" ? "CSV" : "XLSX"}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Preview */}
                {parsedData.length > 0 && (
                    <div className={card}>
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                            <div className="text-sm font-semibold text-slate-800">Parsed Data Preview</div>
                            <div className="text-xs text-slate-500">
                                Showing first <span className="font-semibold text-slate-800">{previewRows.length}</span> rows
                                {parsedData.length > previewRows.length ? (
                                    <span> of {parsedData.length}</span>
                                ) : null}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full table-auto border-collapse">
                                <thead>
                                    <tr className="bg-slate-50">
                                        {previewCols.map((k) => (
                                            <th
                                                key={k}
                                                className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-700"
                                            >
                                                {k}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewRows.map((row, i) => (
                                        <tr key={i} className="border-t border-slate-200 hover:bg-slate-50">
                                            {previewCols.map((k) => (
                                                <td key={k} className="max-w-[360px] truncate px-4 py-3 text-sm text-slate-700">
                                                    {row?.[k] ?? ""}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {parsedData.length > 10 && (
                            <div className="border-t border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                                Preview limited for performance. Submit to upload all rows.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
